/**
 * Document-type taxonomy for the Project Documents Assistant UI.
 *
 * FieldTime's document_type_id column (Migration 066) and RAG's own
 * ingestion contract (fieldtime_contracts.py's OpaqueIdentifier) both treat
 * document_type_id as a fully opaque, freeform identifier -- neither owns a
 * Drawings/Specs/RFIs/Other taxonomy today. These are the canonical values
 * THIS presentation layer introduces for the upload picker and the grouped
 * document list; no new persisted concept/table is created -- both the
 * upload picker and the read-side grouping below key off the SAME existing
 * document_type_id field.
 */
export const DOCUMENT_TYPE_OPTIONS = [
  { id: 'drawing', label: 'Drawing' },
  { id: 'specification', label: 'Specification' },
  { id: 'rfi', label: 'RFI' },
  { id: 'other', label: 'Other' },
];

const GROUP_ORDER = ['Drawings', 'Specs', 'RFIs', 'Other documents'];

// The upload type a file dropped on each section gets.
const DOCUMENT_TYPE_ID_BY_GROUP_NAME = {
  Drawings: 'drawing',
  Specs: 'specification',
  RFIs: 'rfi',
  'Other documents': 'other',
};

// Explicit alias list, not substring matching -- an opaque, unrelated
// document_type_id (e.g. "inspection") must never be misclassified just
// because it happens to contain a matching substring of some other alias.
const GROUP_NAME_BY_ALIAS = {
  drawing: 'Drawings',
  drawings: 'Drawings',
  specification: 'Specs',
  specifications: 'Specs',
  spec: 'Specs',
  specs: 'Specs',
  rfi: 'RFIs',
  rfis: 'RFIs',
  other: 'Other documents',
};

function groupNameForDocumentType(documentTypeId) {
  const value = String(documentTypeId || '')
    .trim()
    .toLowerCase();
  return GROUP_NAME_BY_ALIAS[value] || 'Other documents';
}

/**
 * Group documents into the four display sections, always in GROUP_ORDER
 * and always all four present (even empty), so the pane renders a stable
 * set of sections regardless of what has been uploaded so far.
 * @param {Array} documents
 * @returns {Array<{name: string, documentTypeId: string, documents: Array}>}
 */
export function groupDocumentsByType(documents) {
  const groups = new Map(GROUP_ORDER.map((name) => [name, []]));
  for (const document of documents) {
    const name = groupNameForDocumentType(document.document_type_id);
    groups.get(name).push(document);
  }
  return GROUP_ORDER.map((name) => ({
    name,
    documentTypeId: DOCUMENT_TYPE_ID_BY_GROUP_NAME[name],
    documents: groups.get(name),
  }));
}

// Same fail-closed convention as GROUP_NAME_BY_ALIAS above (never silently
// drop a document with an unrecognized/missing classification value) --
// here applied to discipline_name instead of document_type_id.
const UNSPECIFIED_DISCIPLINE_NAME = 'Unspecified';

function disciplineNameForDocument(document) {
  const value =
    typeof document.discipline_name === 'string'
      ? document.discipline_name.trim()
      : '';
  return value || UNSPECIFIED_DISCIPLINE_NAME;
}

/**
 * Group one type-group's documents by discipline_name -- the DocumentsPane's
 * second, nested level of grouping (within Drawings/Specs/RFIs/Other
 * documents, further split by discipline).
 *
 * Unlike groupDocumentsByType's fixed four-section taxonomy,
 * discipline_name is open-ended (ai_knowledge_disciplines is a data-driven
 * table, not a fixed taxonomy known in advance) -- so, unlike
 * groupDocumentsByType, this does NOT pre-populate a fixed set of groups:
 * only disciplines actually present among `documents` produce a subgroup,
 * and an empty subgroup is never returned. A document with a missing/blank
 * discipline_name still fails closed into a visible "Unspecified" bucket
 * rather than being silently dropped, mirroring groupDocumentsByType's own
 * "Other documents" convention.
 *
 * Subgroups are ordered alphabetically (case-insensitive) by discipline
 * name, except "Unspecified", which always sorts last regardless of where
 * it would otherwise fall alphabetically -- matching "Other documents"
 * always being GROUP_ORDER's last entry above.
 * @param {Array} documents - documents already belonging to one type group
 * @returns {Array<{name: string, documents: Array}>}
 */
export function groupDocumentsByDiscipline(documents) {
  const groups = new Map();
  for (const document of documents) {
    const name = disciplineNameForDocument(document);
    if (!groups.has(name)) {
      groups.set(name, []);
    }
    groups.get(name).push(document);
  }
  return Array.from(groups.entries())
    .sort(([nameA], [nameB]) => {
      if (nameA === UNSPECIFIED_DISCIPLINE_NAME) {
        return nameB === UNSPECIFIED_DISCIPLINE_NAME ? 0 : 1;
      }
      if (nameB === UNSPECIFIED_DISCIPLINE_NAME) {
        return -1;
      }
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    })
    .map(([name, groupDocuments]) => ({ name, documents: groupDocuments }));
}
